/**
 * TransactionDetailSheet
 *
 * A side-panel drawer that renders rich details for a single transaction.
 * Provides inline Edit and Delete action buttons so users can act on
 * transactions from any context (Dashboard, Transactions page, etc.)
 * without navigating away.
 *
 * [T38P0] Dashboard & Recent-Transactions UX Enhancement
 * [T60P0] Enhanced to show all schema fields: emoji, category color,
 *         goal link, currency, merchant, location, receipt, tags, status.
 *         Shared between Dashboard and Transactions page.
 */
import { useMemo, useState, useEffect, useCallback, useRef, type ChangeEvent } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCurrency } from '@/hooks/useCurrency';
import { useTimezone } from '@/hooks/useTimezone';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowLeftRight,
  Pencil,
  Trash2,
  FileText,
  Repeat,
  Clock,
  AlertCircle,
  MapPin,
  Store,
  Target,
  Copy,
  History,
  Package,
  } from 'lucide-react';
import {
  buildTransactionBreakdown,
  normalizeTransactionItems,
  type TransactionBreakdown,
} from '@/lib/transactionBreakdown';
import { ItemsDetailSection } from '@/components/transactions/ItemsDetailSection';
import type { Transaction, Account, Category } from '@/lib/types';
import AuditHistoryPanel from '@/components/common/AuditHistoryPanel';
import api, { endpoints, type ApiResponse } from '@/services/api';
import { useAppDispatch } from '@/features/hooks';
import { unlinkReceipt } from '@/features/receipts/receiptsSlice';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useItemCountPreference } from '@/hooks/useItemCountPreference';
import ReceiptViewerDialog from '@/components/transactions/ReceiptViewerDialog';
import { SaveAsTemplateButton } from '@/components/templates/SaveAsTemplateButton';
import { TransactionFieldRow } from '@/components/transactions/TransactionFieldRow';
import { ALL_EMOJIS } from '@/components/ui/emoji-picker';
import { PRIMARY_FIELDS, SECONDARY_FIELDS } from '@/components/transactions/fieldSpec';

import ReceiptDetailSection from '@/components/transactions/ReceiptDetailSection';

import { SecondaryFieldsDisclosure } from '@/components/transactions/SecondaryFieldsDisclosure';
import { TagInput } from '@/components/ui/tag-input';
import type { ModalEntry } from '@/hooks/useModalStack';



function getEmojiDescription(value: string): string {
  const normalized = value.trim();
  return ALL_EMOJIS.find((entry) => entry.emoji.trim() === normalized)?.label ?? 'Custom icon';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TransactionDetailSheetProps {
  transaction: Transaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accounts?: Account[];
  categories?: Category[];
  onEdit?: (transaction: Transaction, fieldKey?: string) => void;
  // Phase E: dedicated shortcut for modal-stack navigation. When provided, row
  // shortcuts (amount/notes) prefer this over `onEdit` and do NOT close the Sheet.
  onEditShortcut?: (fieldKey: string) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (transaction: Transaction) => void;
  onRefresh?: () => void;
  // [rev16 Phase F] Modal-stack breadcrumb plumbing. Optional — the page will
  // pass `modalStack.stack` in a separate bounded edit. When omitted, the
  // breadcrumb renders nothing (the component guards on an empty stack).
  stack?: ModalEntry[];
}

// ---------------------------------------------------------------------------
// Per-type visual config
// ---------------------------------------------------------------------------

const typeConfig = {
  income: {
    icon: ArrowUpCircle,
    iconColor: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    badgeClass:
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    amountClass: 'text-green-600 dark:text-green-400',
    amountPrefix: '+',
    label: 'Income',
  },
  expense: {
    icon: ArrowDownCircle,
    iconColor: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    badgeClass:
      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    amountClass: 'text-red-600 dark:text-red-400',
    amountPrefix: '-',
    label: 'Expense',
  },
  transfer: {
    icon: ArrowLeftRight,
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    badgeClass:
      'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    amountClass: 'text-blue-600 dark:text-blue-400',
    amountPrefix: '',
    label: 'Transfer',
  },
} as const;

// ---------------------------------------------------------------------------
// Helper: one detail row
// ---------------------------------------------------------------------------

function DetailRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5 break-words">
          {label}
        </p>
        <div className="text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TransactionDetailSheet({
  transaction,
  open,
  onOpenChange,
  accounts = [],
  categories = [],
  onEdit,
  onEditShortcut,
  onDelete,
  onDuplicate,
  onRefresh,
  stack,
}: TransactionDetailSheetProps) {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const { fmt } = useCurrency();
  const { formatInUserTimezone } = useTimezone();
  const { showItemCount } = useItemCountPreference();

  // T117-4: Items always expanded in view mode (click-to-expand removed per T129)
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [receiptDeleting, setReceiptDeleting] = useState(false);
const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
// [R27-D] Detail renders a finalized breakdown only.

  const receiptInputRef = useRef<HTMLInputElement>(null);
  const receiptViewTriggerRef = useRef<HTMLButtonElement>(null);
  // T136P0: Explicit line-item visibility states (loading/error/empty)
  const [lineItems, setLineItems] = useState<Transaction['items'] | null>(null);
  const [lineItemsLoading, setLineItemsLoading] = useState(false);
  const [lineItemsError, setLineItemsError] = useState<string | null>(null);
  // [rev21 P5] Tag summary stats for the read-only TagInput renderer in the tags field row.
  const [tagStats, setTagStats] = useState<{ tag: string; count: number }[]>([]);

  // Receipt viewer dialog state
  const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);

  const loadLineItems = useCallback(async () => {
    if (!transaction?.id) return;

    setLineItemsLoading(true);
    setLineItemsError(null);
    try {
      const res = await api.get<ApiResponse<Transaction>>(endpoints.transactions.get(transaction.id));
      const normalized = normalizeTransactionItems(res.data?.data?.items) as Transaction['items'];
      setLineItems(normalized);
    } catch {
      setLineItemsError('Could not load line items. Please try again.');
      setLineItems([]);
    } finally {
      setLineItemsLoading(false);
    }
  }, [transaction?.id]);

  useEffect(() => {
    if (!open || !transaction) {
      setLineItems(null);
setLineItemsLoading(false);
      setLineItemsError(null);
      // [R27-D] No preview disclosure state to reset; modal closes with the items signal intact.

      return;
    }

    const hasProvidedItems = Array.isArray(transaction.items) && transaction.items.length > 0;
    const mayHaveItems = !!(transaction.hasItems || hasProvidedItems);

    if (!mayHaveItems) {
      setLineItems(null);
      setLineItemsLoading(false);
      setLineItemsError(null);
      return;
    }

    if (hasProvidedItems) {
      setLineItems(normalizeTransactionItems(transaction.items) as Transaction['items']);
      setLineItemsLoading(false);
      setLineItemsError(null);
      return;
    }

    void loadLineItems();
  }, [open, transaction, loadLineItems]);



  // T126-F4: Fetch receipt document ID and thumbnail when transaction has receipt
  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    setReceiptId(null);


    if (!open || !transaction?.receiptAttachment?.filename) return;

    api
      .get(endpoints.receipts.list, {
        params: { transactionId: transaction.id, limit: 1 },
      })
      .then((res) => {
        if (cancelled) return;
        const receipts = (
          res.data as { data?: { _id: string }[] }
        )?.data;
        if (receipts && receipts.length > 0) {
          const rid = receipts[0]._id;
          setReceiptId(rid);
          return api.get(endpoints.receipts.thumbnail(rid), {
            responseType: 'blob',
          });
        }
        return undefined;
      })
      .then((thumbRes) => {
        if (cancelled || !thumbRes) return;
        blobUrl = URL.createObjectURL(thumbRes.data as Blob);

      })
      .catch(() => {
        /* ignore */
      });

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [open, transaction?.id, transaction?.receiptAttachment?.filename]);

  // [rev21 P5] Fetch tagStats on open using the same response shape as TransactionForm.
  // Mirrors the Form's `/transactions/analytics/tags-summary` fetch so the read-only TagInput
  // renderer in the tags field row can display the normalized count badge.
  useEffect(() => {
    if (!open) {
      setTagStats([]);
      return;
    }

    const frameId = requestAnimationFrame(() => {
      api
        .get('/transactions/analytics/tags-summary')
        .then((res) => {
          const tags = (res.data as { data?: { tags?: { tag: string; count: number }[] } })?.data?.tags ?? [];
          setTagStats(tags);
        })
        .catch(() => {
          /* ignore — read-only display falls back to chips without count badge */
        });
    });

    return () => cancelAnimationFrame(frameId);
  }, [open]);

  // T126-F4: Receipt handlers
  const handleReceiptUpload = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !transaction) return;
      e.target.value = '';

      const MAX_SIZE = 10 * 1024 * 1024;
      const ALLOWED = [
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/heic',
        'application/pdf',
      ];
      if (!ALLOWED.includes(file.type) || file.size > MAX_SIZE) return;


      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('transactionId', transaction.id);
        const res = await api.post(endpoints.receipts.upload, formData, { timeout: 60000 });
        const newReceipt = (res.data as { data?: { _id?: string; id?: string; thumbnailPath?: string } })?.data;
        const rid = newReceipt?._id ?? newReceipt?.id ?? null;
        if (rid) {
          setReceiptId(rid);
        }
        onRefresh?.();
        toast({ title: 'Receipt uploaded', description: 'Receipt attached successfully.' });
      } catch (err) {
        const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          ?? (err instanceof Error ? err.message : 'Upload failed');
        toast({ title: 'Receipt upload failed', description: msg, variant: 'destructive' });
      }
    },
    [transaction, onRefresh, toast],
  );

  // Helper to lazily fetch receiptId if it wasn't resolved during init (e.g. 5xx)
  const ensureReceiptId = useCallback(async (): Promise<string | null> => {
    if (receiptId) return receiptId;
    if (!transaction?.id) return null;
    try {
      const res = await api.get(endpoints.receipts.list, { params: { transactionId: transaction.id, limit: 1 } });
      const receipts = (res.data as { data?: { _id: string }[] })?.data;
      if (receipts && receipts.length > 0) {
        setReceiptId(receipts[0]._id);
        return receipts[0]._id;
      }
    } catch {
      /* will be handled by caller */
    }
    return null;
  }, [receiptId, transaction?.id]);

  const handleReceiptView = useCallback(async () => {
    const rid = await ensureReceiptId();
    if (!rid) {
      toast({ title: 'Receipt unavailable', description: 'Could not load receipt. Please try again later.', variant: 'destructive' });
      return;
    }
    setReceiptViewerOpen(true);
  }, [ensureReceiptId, toast]);

  const handleReceiptDelete = useCallback(async () => {
    const rid = await ensureReceiptId();
    if (!rid) {
      toast({ title: 'Remove failed', description: 'Could not locate receipt to remove.', variant: 'destructive' });
      return;
    }
    setReceiptDeleting(true);
    try {
      // Unlink (preserves the file in /receipts) instead of hard-deleting.
      // Backend: POST /receipts/:id/unlink (Round-2 §5).
      await dispatch(unlinkReceipt(rid)).unwrap();
      setReceiptId(null);

      onRefresh?.();
      toast({ title: 'Receipt unlinked', description: 'The file remains in your receipt library.' });
    } catch (err) {
      const msg = (err as { message?: string })?.message
        ?? (err instanceof Error ? err.message : 'Could not remove the receipt link.');
      toast({ title: 'Remove failed', description: msg, variant: 'destructive' });
    } finally {
      setReceiptDeleting(false);
      setUnlinkConfirmOpen(false);
    }
  }, [ensureReceiptId, dispatch, onRefresh, toast]);

  // [R27-D] Single shared finalized breakdown. Replaces the local per-row
  // net / gross / discount math that used to feed the items-detail-net test ID.
  // The shared breakdown helper is consumed by both TransactionForm and this Detail view,
  // so allocation state stays consistent between the two surfaces.
  // Kept above the early `!transaction` return to satisfy react-hooks/rules-of-hooks.
  const breakdown = useMemo<TransactionBreakdown>(
    () =>
      buildTransactionBreakdown({
        amount: transaction?.amount,
        transactionDiscountAmount: transaction?.discountAmount,
        items: lineItems ?? [],
      }),
    [transaction?.amount, transaction?.discountAmount, lineItems],
  );

  if (!transaction) return null;



  const config = typeConfig[transaction.type] ?? typeConfig.expense;
  const Icon = config.icon;

  const accountObj = accounts.find((a) => a.id === transaction.accountId) ?? null;
  const accountName = accountObj?.name ?? null;
  const accountEmoji = accountObj?.emoji ?? '';
  const categoryObj = categories.find(
    (c) => c.id === transaction.categoryId
  );
  const categoryEmoji = categoryObj?.emoji ?? '';
  const categoryFallbackName =
    typeof transaction.category === 'string'
      ? transaction.category
      : transaction.categoryId ?? '';
  const categoryDisplayName = categoryObj?.name ?? categoryFallbackName;
  const hasLineItemsSignal = !!(transaction.hasItems || Array.isArray(transaction.items));
  const resolvedLineItems = lineItems ?? [];
  const lineItemsCount = resolvedLineItems.length;
  const hasResolvedLineItems = lineItemsCount > 0;
  const showLineItemsSection = hasLineItemsSignal || lineItemsLoading || !!lineItemsError;


  // [rev16 P0 regression] Stack-aware footer Edit: when the parent supplies a
  // modal stack (rev16 flow), the parent owns Detail/Edit navigation via push/pop,
  // so the footer Edit must NOT close the Sheet first — closing would drop the Detail
  // entry before the parent could push an Edit entry on top, and the form-back-btn round-trip
  // would have no Detail to return to. The parent captures scrollTop and patches the existing
  // detail entry's meta via `patchMeta`, then pushes an Edit entry — yielding `[detail, edit]`.
  // Standalone callers (no stack) keep the legacy close-then-edit behavior so Dashboard.tsx and
  // LegacyDashboard.tsx's RecentTransactionsCard fallback is unaffected.
  const handleEdit = () => {
    if (stack && stack.length > 0) {
      // Stack-aware: invoke the edit callback without closing the Sheet so the parent can push an Edit entry on top of the existing Detail entry.
      onEdit?.(transaction);
      return;
    }
    // Standalone: close-then-edit preserves the legacy behavior for RecentTransactionsCard fallback.
    onOpenChange(false);
    onEdit?.(transaction);
  };

  const handleDelete = () => {
    onOpenChange(false);
    onDelete?.(transaction.id);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        closeTestId="detail-close-btn"
        className="w-[calc(100vw-3rem)] sm:max-w-[480px] lg:max-w-[860px] h-[100dvh] flex flex-col p-0 gap-0 overflow-hidden pb-[env(safe-area-inset-bottom)]"
      >
        {/* [rev18] Compact header shell — collapses hero identity + amount rail into a single row. Stays within the 88px compact-header budget across every viewport × theme. The `modal-header-shell` marker is the canonical contract for the header region; the `detail-amount-row` inner row remains an explicit horizontal rail so the overflow scanner can opt it out. The hero identity shortcut (former `txn-field-edit-link-description` pencil) was removed in rev18 — the footer `detail-sheet-edit-btn` is now the sole Edit affordance per the AC4 contract. */}
<div
data-testid="modal-header-shell"
className="px-4 py-2 pr-16 flex items-center gap-3 shrink-0 border-b bg-background relative z-10"
>
<div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-xl bg-white/80 dark:bg-black/20 shadow-sm">
{transaction.emoji ? (
<span className="text-xl leading-none">{transaction.emoji}</span>
) : (
<Icon className={`h-5 w-5 ${config.iconColor}`} />
)}
</div>
<div className="min-w-0 flex-1 space-y-1">
<div data-testid="detail-title" className="min-w-0">
<SheetTitle className="min-w-0 text-base font-semibold leading-tight line-clamp-2 break-words [overflow-wrap:anywhere]">
{transaction.description}
</SheetTitle>
</div>
<div data-horizontal-rail="true" data-testid="detail-meta-row" className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
<div
data-testid="detail-amount-row"
className="flex flex-nowrap items-center gap-2 min-w-max px-1"
>
<Badge data-testid="detail-type-badge" className={`shrink-0 text-[10px] ${config.badgeClass} border-0`}>
{config.label}
</Badge>
<span
className={`text-base font-bold tabular-nums whitespace-nowrap shrink-0 ${config.amountClass}`}
>
{config.amountPrefix}{fmt(Math.abs(transaction.amount))}
</span>
{showItemCount && hasResolvedLineItems && (
<Badge variant="secondary" className="text-xs gap-1 shrink-0">
<Package className="h-3 w-3" />
{lineItemsCount} items
</Badge>
)}
{transaction.isPending && (
<Badge
variant="outline"
className="gap-1 text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 shrink-0"
>
<AlertCircle className="h-3 w-3" />
Pending
</Badge>
)}
{transaction.isRecurring && (
<Badge
variant="outline"
className="gap-1 text-purple-600 border-purple-300 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-700 shrink-0"
>
<Repeat className="h-3 w-3" />
{transaction.recurringPattern
? `${transaction.recurringPattern.frequency.charAt(0).toUpperCase()}${transaction.recurringPattern.frequency.slice(1)}${(transaction.recurringPattern.interval ?? 1) > 1 ? ` (every ${transaction.recurringPattern.interval})` : ''}`
: 'Recurring'}
</Badge>
)}
{transaction.goal && (
<Badge
variant="outline"
className="gap-1 text-sky-600 border-sky-300 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-700 shrink-0"
>
<Target className="h-3 w-3" />
Goal Contribution
</Badge>
)}
</div>
</div>
</div>
<SheetDescription className="sr-only">
Transaction details
</SheetDescription>
</div>
        {/* ---------------------------------------------------------------- */}
        {/* Tabs: Details + History                                           */}
        {/* ---------------------------------------------------------------- */}
        <Tabs defaultValue="details" className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="px-6 pt-2 shrink-0">
            <TabsList className="w-full">
              <TabsTrigger value="details" className="flex-1 gap-1.5">
                <FileText className="h-3.5 w-3.5" />
                Details
              </TabsTrigger>
              <TabsTrigger value="history" className="flex-1 gap-1.5">
                <History className="h-3.5 w-3.5" />
                History
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="details" data-testid="detail-scroll-container" className="flex-1 min-h-0 overflow-y-auto mt-0 scrollbar-elegant touch-pan-y overscroll-contain md:[scrollbar-gutter:stable]">
          <div className="space-y-4">
            <div className="px-6 py-5 space-y-4 min-w-0">
            {/* [rev17 A3] Canonical PRIMARY_FIELDS rows - read-only mirror of the form schema.
                Renders every registry entry (even when optional values are absent) so the
                Phase B test selector `txn-field-row-{key}` resolves for all 10 canonical keys.
                Edit shortcuts are wired for the AC5 row-level keys (amount, date, accountId,
                categoryId, tags, notes, receipt, emoji) via the same stack/legacy callbacks.
                `items` is intentionally excluded - it has its own itemized section route
                (ItemsDetailSection) and is not a generic focus target. The legacy detail-row
                blocks below are preserved and remain outside this wrapper. */}
            <div data-testid="detail-fields" className="space-y-0">
              {(() => {
                const detailValues: Record<string, string> = {
                  type: typeConfig[transaction.type]?.label ?? transaction.type,
                  amount: `${config.amountPrefix}${fmt(Math.abs(transaction.amount))}`,
                  date: formatInUserTimezone(transaction.date, 'long'),
                  accountId: accountName ?? transaction.accountId,
                  categoryId:
                    categoryObj?.name
                    ?? (typeof transaction.category === 'string'
                      ? transaction.category
                      : transaction.categoryId)
                    ?? '',
                  tags: (transaction.tags ?? []).join(', '),
                  items: resolvedLineItems.map((i) => i.description).join(', '),
                  notes: transaction.notes ?? '',
                  receipt: transaction.receiptAttachment?.filename ?? '',
                  emoji: transaction.emoji ?? '',
                };
                // [rev17 A3] AC5 row-level shortcut keys. `items` is intentionally excluded - it has its own itemized section route (ItemsDetailSection) and is not a generic focus target. `type` is intentionally excluded per AC5 scope (amount, date, account, category, tags, notes, receipt, icon, transaction name).
                const shortcutKeys = new Set(['amount', 'date', 'accountId', 'categoryId', 'tags', 'notes', 'receipt', 'emoji']);
                return PRIMARY_FIELDS.filter((spec) => spec.key !== 'items' && spec.key !== 'receipt').map((spec) => {
                  const readSpec: typeof spec = (() => {
                    if (spec.key === 'accountId') {
                      return {
                        ...spec,
                        readRenderer: () => (
                          <span data-testid="txn-field-cell-emoji-accountId" className="inline-flex items-baseline gap-1">
                            {accountEmoji ? <span aria-hidden="true">{accountEmoji}</span> : null}
                            <span>{accountName ?? transaction.accountId}</span>
                          </span>
                        ),
                      };
                    }
                    if (spec.key === 'categoryId') {
                      return {
                        ...spec,
                        readRenderer: () => (
                          <span data-testid="txn-field-cell-emoji-categoryId" className="inline-flex items-baseline gap-1">
                            {categoryEmoji ? <span aria-hidden="true">{categoryEmoji}</span> : null}
                            <span>{categoryDisplayName || ''}</span>
                          </span>
                        ),
                      };
                    }
                    if (spec.key === 'tags') {
                      return {
                        ...spec,
                        readRenderer: () => (
                          <TagInput value={transaction.tags ?? []} onChange={() => undefined} tagStats={tagStats} disabled />
                        ),
                      };
                    }
if (spec.key === 'emoji') {
                      // Keep the pencil in the fixed label-cell slot and show
                      // the selected glyph plus its descriptive catalogue name
                      // in the existing value cell.
                      return {
                        ...spec,
                        readRenderer: (val) => {
                          if (!val) return '—';
                          const emoji = String(val);
                          return (
                            <span data-testid="txn-field-cell-emoji" className="inline-flex items-center gap-2">
                              <span aria-hidden="true">{emoji}</span>
                              <span>{getEmojiDescription(emoji)}</span>
                            </span>
                          );
                        },
                      };
                    }
                    return spec;
                  })();
                  return (
                    <TransactionFieldRow
                      key={spec.key}
                      field={readSpec}
                      mode="read"
                      value={detailValues[spec.key] ?? ''}
                      onEditShortcut={
                        shortcutKeys.has(spec.key)
                          ? () =>
                              onEditShortcut
                                ? onEditShortcut(spec.key)
                                : onEdit
                                  ? onEdit(transaction, spec.key)
                                  : undefined
                          : undefined
                      }
readLabelAdornment={undefined}
                    />
                  );
                })
              })()}
            </div>

            {/* [R27-D] Finalized Detail breakdown. Renders the shared ItemsDetailSection
                so Detail consumes the same helper, row primitive, and summary leaf as
                the editing surface. The host keeps the rev16 test IDs
                (items-detail-section / list / row-{index} / net / empty / loading /
                error) and the new finalized-breakdown-* IDs from the summary leaf. */}
            {showLineItemsSection && (
              <ItemsDetailSection
                items={resolvedLineItems}
                breakdown={breakdown}
                fmt={fmt}
                loading={lineItemsLoading}
                error={lineItemsError}
                categories={categories}
                transactionCategoryId={transaction.categoryId}
                transactionCategoryName={categoryDisplayName}
                transactionCategoryEmoji={categoryEmoji}
              />
            )}

            {/* [T60P0] Merchant */}
            {transaction.merchant && (
              <DetailRow icon={Store} label="Merchant">
                {transaction.merchant}
              </DetailRow>
            )}

            {/* [T60P0] Location */}
            {transaction.location && (
              <DetailRow icon={MapPin} label="Location">
                {transaction.location}
              </DetailRow>
            )}
            {/* [rev16 Phase F] Receipt input — hidden file input reachable by
                ReceiptDetailSection's `onReplace` callback. Kept in the DOM (not visible)
                so the upload handler stays live after the legacy Receipt DetailRow was removed. */}
            <input
              ref={receiptInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={handleReceiptUpload}
            />

            {/* [rev16 Phase F] ReceiptDetailSection — canonical read-only receipt block.
                Replaces the legacy `transaction-receipt-section` DetailRow with the shared leaf component. */}
            <ReceiptDetailSection
              receipt={transaction.receiptAttachment ?? null}
              fmtSize={(bytes: number) =>
                bytes >= 1024 * 1024
                  ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
                  : `${(bytes / 1024).toFixed(0)} KB`
              }
              onView={handleReceiptView}
              onReplace={() => receiptInputRef.current?.click()}
              onRemove={() => setUnlinkConfirmOpen(true)}
            />

            {/* [T60P0] Linked transaction (for transfers) */}
            {transaction.linkedTransactionId && (
              <DetailRow icon={ArrowLeftRight} label="Linked Transfer">
                <span className="text-muted-foreground font-mono text-xs truncate">
                  {transaction.linkedTransactionId}
                </span>
              </DetailRow>
            )}

            {/* [rev16 Phase F] SecondaryFieldsDisclosure — canonical read-mode "More ▾" panel
                for currency + discountAmount. Replaces the legacy Currency DetailRow. */}
            <SecondaryFieldsDisclosure
              fields={SECONDARY_FIELDS}
              mode="read"
              defaultOpen={false}
              autoOpen={typeof transaction.discountAmount === 'number' && transaction.discountAmount > 0}
              values={{
                currency: transaction.currency ?? '',
                discountAmount:
                  typeof transaction.discountAmount === 'number'
                    ? fmt(transaction.discountAmount)
                    : '',
              }}
              onEditShortcut={(key) =>
                onEditShortcut
                  ? onEditShortcut(key)
                  : onEdit
                    ? onEdit(transaction, key)
                    : undefined
              }
            />

            <Separator className="my-2" />

            {/* Timestamps */}
            <div className="space-y-3">
              <DetailRow icon={Clock} label="Created">
                <span className="text-muted-foreground">
                  {formatInUserTimezone(transaction.createdAt, 'medium')}
                </span>
              </DetailRow>
              {transaction.updatedAt !== transaction.createdAt && (
                <DetailRow icon={Clock} label="Last Updated">
                  <span className="text-muted-foreground">
                    {formatInUserTimezone(transaction.updatedAt, 'medium')}
                  </span>
                </DetailRow>
              )}
            </div>
            </div>

          </div>
          </TabsContent>

          <TabsContent value="history" className="flex-1 overflow-y-auto mt-0 scrollbar-elegant touch-pan-y overscroll-contain md:[scrollbar-gutter:stable]">
            <AuditHistoryPanel
              resource="transaction"
              resourceId={transaction.id}
              onRollback={onRefresh}
            />
          </TabsContent>
        </Tabs>

        {/* ---------------------------------------------------------------- */}
        {/* Footer actions                                                    */}
        {/* ---------------------------------------------------------------- */}
{(onEdit || onDelete || onDuplicate) && (
<SheetFooter
data-testid="detail-sheet-footer"
    data-horizontal-rail="true"
    className="sticky bottom-0 border-t bg-background p-0 gap-0 flex-row flex-nowrap overflow-hidden"
>
  <div
    data-testid="detail-footer-scroll-rail"
    data-horizontal-rail="true"
    className="flex flex-row flex-nowrap items-center gap-1.5 min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden px-3 sm:px-4 py-2"
  >
<div className="flex min-w-max items-center gap-1.5">
            <SaveAsTemplateButton
              transaction={transaction}
              variant="outline"
              size="sm"
              className="shrink-0 min-w-[80px] min-h-[44px]"
            />
            {onEdit && (
              <Button
                size="sm"
                onClick={handleEdit}
                className="shrink-0 min-w-[80px] min-h-[44px]"
                data-testid="detail-sheet-edit-btn"
              >
                <Pencil className="h-4 w-4 mr-1.5" />
                Edit Transaction
              </Button>
            )}
            {onDuplicate && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 min-w-[80px] min-h-[44px]"
                onClick={() => { onOpenChange(false); onDuplicate(transaction); }}
              >
                <Copy className="h-4 w-4 mr-1.5" />
                Duplicate
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 min-w-[80px] min-h-[44px] text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete
              </Button>
            )}
</div>
  </div>
  <div data-testid="detail-footer-scroll-fade" aria-hidden="true" className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent" />
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>

      <ReceiptViewerDialog
        open={receiptViewerOpen}
        onOpenChange={setReceiptViewerOpen}
        receiptId={receiptId}
        returnFocusRef={receiptViewTriggerRef}
        metadata={transaction?.receiptAttachment ? {
          filename: transaction.receiptAttachment.filename,
          size: transaction.receiptAttachment.size,
          mimetype: transaction.receiptAttachment.mimetype,
        } : undefined}
      />

      <AlertDialog open={unlinkConfirmOpen} onOpenChange={setUnlinkConfirmOpen}>
        <AlertDialogContent data-testid="transaction-receipt-unlink-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove receipt from this transaction?</AlertDialogTitle>
            <AlertDialogDescription>
              The file stays in your receipt library and can be re-linked later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={receiptDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReceiptDelete}
              disabled={receiptDeleting}
              data-testid="transaction-receipt-unlink-confirm-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {receiptDeleting ? 'Removing…' : 'Remove link'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
